import React, { useState, useEffect, useMemo } from "react";
import { TreeView, TreeItem } from "@mui/lab";
import Checkbox from "@mui/material/Checkbox";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const RETURN_TYPE = "Emplacement";

const EmplacementTree2 = ({ data, onChange, language = "fr", selectedIds = [] }) => {
  const [checked, setChecked] = useState(new Set());

  // 🔹 Fonction pour récupérer tous les descendants
  const getAllDescendants = (node) => {
    let result = [];
    if (node.children) {
      node.children.forEach((child) => {
        result.push(child);
        result = result.concat(getAllDescendants(child));
      });
    }
    return result;
  };

  const getKey = (node) => `${node.type}-${node.id}`;
  const getLabel = (node) => (language === "fr" ? node.labelFr : node.labelEn);

  // 🔹 Initialisation de checked avec selectedIds
  useEffect(() => {
    const initialChecked = new Set(selectedIds.map((id) => `${RETURN_TYPE}-${id}`));

    // ✅ Pré-cocher tous les parents si tous leurs enfants sont cochés
    const checkParents = (nodes) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          checkParents(node.children);
          const descendants = getAllDescendants(node);
          if (descendants.every((child) => initialChecked.has(getKey(child)))) {
            initialChecked.add(getKey(node));
          }
        }
      });
    };
    checkParents(data);

    setChecked(initialChecked);
  }, [selectedIds, data]);

  // 🔹 Vérifie si un node est coché
  const isChecked = (node) => {
    if (!node.children || node.children.length === 0) return checked.has(getKey(node));
    const descendants = getAllDescendants(node);
    return descendants.every((child) => checked.has(getKey(child)));
  };

  // 🔹 Vérifie si un node est indéterminé
  const isIndeterminate = (node) => {
    if (!node.children || node.children.length === 0) return false;
    const descendants = getAllDescendants(node);
    const checkedCount = descendants.filter((child) => checked.has(getKey(child))).length;
    return checkedCount > 0 && checkedCount < descendants.length;
  };

  // 🔹 Toggle parent / enfants
  const handleToggle = (node) => {
    const key = getKey(node);
    const descendants = getAllDescendants(node);

    const newChecked = new Set(checked);
    const shouldCheck = !isChecked(node); // coche si tout n'est pas coché

    if (shouldCheck) {
      descendants.forEach((child) => newChecked.add(getKey(child)));
      if (!node.children || node.children.length === 0) newChecked.add(key);
    } else {
      descendants.forEach((child) => newChecked.delete(getKey(child)));
      if (!node.children || node.children.length === 0) newChecked.delete(key);
    }

    setChecked(newChecked);

    const selectedEmplacements = [...newChecked]
      .filter((k) => k.startsWith(`${RETURN_TYPE}-`))
      .map((k) => Number(k.split("-")[1]));

    onChange?.(selectedEmplacements);
  };

  const renderNode = (node) => {
    const key = getKey(node);
    return (
      <TreeItem
        key={key}
        nodeId={key}
        label={
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Checkbox
              size="small"
              checked={isChecked(node)}
              indeterminate={isIndeterminate(node)}
              onChange={() => handleToggle(node)}
              onClick={(e) => e.stopPropagation()}
            />
            <span>{getLabel(node)}</span>
          </div>
        }
      >
        {node.children?.map((child) => renderNode(child))}
      </TreeItem>
    );
  };

  const defaultExpanded = useMemo(() => data.map((node) => getKey(node)), [data]);

  return (
    <TreeView
      multiSelect
      defaultExpanded={defaultExpanded}
      defaultExpandIcon={<ChevronRightIcon />}
      defaultCollapseIcon={<ExpandMoreIcon />}
    >
      {data.map((root) => renderNode(root))}
    </TreeView>
  );
};

export default EmplacementTree2;
